const ProjectNavigationTarget = {
  generalProjectData: 'generalProjectData',
  projectStructure: 'projectStructure',
  fieldValues: 'fieldValues',
  fieldVisibility: 'fieldVisibility',
  workflowConfiguration: 'workflowConfiguration',
  organizationData: 'organizationData',
} as const;

/** The query parameter of a documentation link that names the version to open; the link format mobile uses too. */
export const DOCUMENTATION_VERSION_QUERY_PARAM = 'version';

const documentationFilePath = (projectId: string, fileId: string): string =>
  `/project/${projectId}/documentation/file/${fileId}`;

export const APP_PATHS = {
  dashboard: '/dashboard',
  login: '/login',
  redirect: '/redirect',
  issue: {
    root: '/issue',
    single: (projectId: string, issueId: string) =>
      `/project/${projectId}/issue/${issueId}`,
    event: (projectId: string, issueId: string, eventId: string) =>
      `/project/${projectId}/issue/${issueId}/event/${eventId}`,
    decision: (projectId: string, issueId: string, decisionId: string) =>
      `/project/${projectId}/issue/${issueId}/decision/${decisionId}`,
    document: {
      onIssue: (projectId: string, issueId: string, documentId: string) =>
        `/project/${projectId}/issue/${issueId}/document/${documentId}`,
      onEvent: (
        projectId: string,
        issueId: string,
        eventId: string,
        documentId: string
      ) =>
        `/project/${projectId}/issue/${issueId}/event/${eventId}/document/${documentId}`,
    },
    documentation: (projectId: string, issueId: string, versionId: string) =>
      `/project/${projectId}/issue/${issueId}/documentation/${versionId}`,
  },
  site: {
    root: '/site',
    single: (siteId: string) => `/site/${siteId}`,
  },
  company: {
    root: '/company',
    create: '/company/create',
    details: (companyId: string) => `/company/${companyId}`,
    edit: (companyId: string) => `/company/${companyId}/edit`,
  },
  contract: {
    root: '/contract',
    create: '/contract/create',
    details: (contractId: string) => `/contract/${contractId}`,
    edit: (contractId: string) => `/contract/${contractId}/edit`,
  },
  inspection: {
    root: '/inspection',
    create: (inspectionTemplateId: string) =>
      `/inspectionTemplate/${inspectionTemplateId}/inspection/create`,
    single: (projectId: string, inspectionId: string) =>
      `/project/${projectId}/inspection/${inspectionId}`,
    edit: (projectId: string, inspectionId: string) =>
      `/project/${projectId}/inspection/${inspectionId}/edit`,
    protocolItem: (
      projectId: string,
      inspectionId: string,
      protocolId: string
    ) =>
      `/project/${projectId}/inspection/${inspectionId}/checklist/${protocolId}`,
    section: (
      projectId: string,
      inspectionId: string,
      sectionId: string
    ) =>
      `/project/${projectId}/inspection/${inspectionId}/section/${sectionId}`,
    templateSection: (
      projectId: string,
      inspectionId: string,
      templateId: string,
      checklistSectionId: string
    ) =>
      `/project/${projectId}/inspection/${inspectionId}/template/${templateId}/section/${checklistSectionId}`,
    templateInInspection: (
      projectId: string,
      inspectionId: string,
      templateId: string
    ) =>
      `/project/${projectId}/inspection/${inspectionId}/template/${templateId}`,
    complianceCheck: (
      projectId: string,
      inspectionId: string,
      protocolItemId: string
    ) =>
      `/project/${projectId}/inspection/${inspectionId}/protocolItem/${protocolItemId}/complianceCheck`,
    document: {
      onInspection: (
        projectId: string,
        inspectionId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/document/${documentId}`,
      onTemplateInInspection: (
        projectId: string,
        inspectionId: string,
        templateId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/template/${templateId}/document/${documentId}`,
      onProtocol: (
        projectId: string,
        inspectionId: string,
        protocolId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/checklist/${protocolId}/document/${documentId}`,
      onComplianceCheck: (
        projectId: string,
        inspectionId: string,
        protocolItemId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/protocolItem/${protocolItemId}/complianceCheck/document/${documentId}`,
      onSection: (
        projectId: string,
        inspectionId: string,
        sectionId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/section/${sectionId}/document/${documentId}`,
      onTemplateSectionInInspection: (
        projectId: string,
        inspectionId: string,
        templateId: string,
        checklistSectionId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspection/${inspectionId}/template/${templateId}/section/${checklistSectionId}/document/${documentId}`,
    },
  },
  inspectionTemplate: {
    root: (projectId: string) =>
      `/project/${projectId}/inspectionTemplate`,
    create: '/inspectionTemplate/create',
    single: (projectId: string, inspectionTemplateId: string) =>
      `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}`,
    edit: (inspectionTemplateId: string) =>
      `/inspectionTemplate/${inspectionTemplateId}/edit`,
    checklistItem: (
      projectId: string,
      inspectionTemplateId: string,
      checklistItemId: string
    ) =>
      `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/checklist/${checklistItemId}`,
    section: (
      projectId: string,
      inspectionTemplateId: string,
      sectionId: string
    ) =>
      `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/section/${sectionId}`,
    document: {
      onTemplate: (
        projectId: string,
        inspectionTemplateId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/document/${documentId}`,
      onGeneralNorm: (
        projectId: string,
        inspectionTemplateId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/generalNorm/document/${documentId}`,
      onChecklistItem: (
        projectId: string,
        inspectionTemplateId: string,
        checklistId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/checklist/${checklistId}/document/${documentId}`,
      onChecklistItemNorm: (
        projectId: string,
        inspectionTemplateId: string,
        checklistItemId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/checklistItem/${checklistItemId}/norm/document/${documentId}`,
      onSection: (
        projectId: string,
        inspectionTemplateId: string,
        sectionId: string,
        documentId: string
      ) =>
        `/project/${projectId}/inspectionTemplate/${inspectionTemplateId}/section/${sectionId}/document/${documentId}`,
    },
  },
  project: {
    edit: (projectId: string) => `/project/${projectId}/edit`,
    settings: {
      root: '/projectSettings',
      organizationDetails: `/projectSettings?target=${ProjectNavigationTarget.organizationData}`,
      projectDetails: `/projectSettings?target=${ProjectNavigationTarget.generalProjectData}`,
      projectStructure: `/projectSettings?target=${ProjectNavigationTarget.projectStructure}`,
      fieldValues: `/projectSettings?target=${ProjectNavigationTarget.fieldValues}`,
      fieldVisibility: `/projectSettings?target=${ProjectNavigationTarget.fieldVisibility}`,
      workflowConfiguration: `/projectSettings?target=${ProjectNavigationTarget.workflowConfiguration}`,
      workflowCreator: (projectId: string, workflowId: string) =>
        `/project/${projectId}/workflow/${workflowId}`,
    },
    select: '/select-project',
  },
  user: {
    root: '/user',
    create: '/user/create',
    current: '/me',
    single: (userId: string) => `/user/${userId}`,
    edit: (userId: string) => `/user/${userId}/edit`,
  },
  fieldsSettings: {
    root: '/fieldsSettings',
    customField: (fieldId: string) => `/fieldsSettings/${fieldId}`,
  },
  documentation: {
    root: (projectId: string) => `/project/${projectId}/documentation`,
    file: documentationFilePath,
    /** `file` opening one version of the documentation; without a version it is the plain `file` link (PT-7326). */
    fileVersion: (projectId: string, fileId: string, versionId?: string) =>
      versionId
        ? `${documentationFilePath(projectId, fileId)}?${DOCUMENTATION_VERSION_QUERY_PARAM}=${encodeURIComponent(versionId)}`
        : documentationFilePath(projectId, fileId),
    dir: (projectId: string, directoryId: string) =>
      `/project/${projectId}/documentation/dir/${directoryId}`,
  },
  report: {
    download: (reportId: string) => `/report/${reportId}/download`,
  },
  analytics: {
    single: (chartId: string) => `/analytics/${chartId}`,
    root: `/analytics`,
  },
  organization: {
    edit: (organizationId: string) =>
      `/organization/${organizationId}/edit`,
  },
  gdpr: (projectId: string) => `/project/${projectId}/gdpr/clause`,
} as const;

const DOCUMENTATION_PATH_REGEX = /\/documentation(?:\/|$)/;
const DOCUMENTATION_ROOT_PATH_REGEX = /\/documentation$/;
const DOCUMENTATION_FILE_PATH_REGEX = /\/documentation\/file\/[^/]+/;
const DOCUMENTATION_DIRECTORY_PATH_REGEX = /\/documentation\/dir\/[^/]+/;

export function isDocumentationPath(pathname: string): boolean {
  return DOCUMENTATION_PATH_REGEX.test(pathname);
}

export function isDocumentationRootPath(pathname: string): boolean {
  return DOCUMENTATION_ROOT_PATH_REGEX.test(pathname);
}

export function isDocumentationFilePath(pathname: string): boolean {
  return DOCUMENTATION_FILE_PATH_REGEX.test(pathname);
}

export function isDocumentationDirectoryPath(pathname: string): boolean {
  return DOCUMENTATION_DIRECTORY_PATH_REGEX.test(pathname);
}

export type PathParams<T> = T extends (...args: infer P) => string
  ? P
  : never;

export type RoutePaths = typeof APP_PATHS;
